import { ref, type Ref } from 'vue'

/**
 * Persistent offline queue with idempotent replay for kiosk session commands.
 *
 * Every session mutation (start / pause / resume / finish) carries a stable
 * `client_event_id`. When a request fails because the device is offline (or a
 * network error), the action is persisted to localStorage with that same
 * `client_event_id` so a later replay is deduplicated by the backend
 * (`findWorkerSessionEventByClientEventId` returns the stored response instead
 * of applying the mutation twice).
 *
 * The queue auto-flushes when the browser goes back online and exposes a manual
 * `flush()` for a "Retry now" button.
 */
export interface QueuedAction {
  /** Unique id of the queue entry itself. */
  id: string
  /** 'start' | 'pause' | 'resume' | 'finish' */
  type: string
  /** Stable idempotency key, reused verbatim on retries. */
  clientEventId: string
  payload: Record<string, unknown>
  createdAt: string
}

export interface OfflineQueueOptions {
  /** localStorage key under which the pending queue is persisted. */
  storageKey: string
  /** Executes one queued action; must throw on failure so it stays queued. */
  execute: (action: QueuedAction) => Promise<void>
  /** When true (default), permanent failures drop the item instead of retrying forever. */
  isRetryable?: (error: unknown) => boolean
}

export interface UseOfflineQueueReturn {
  queue: Ref<QueuedAction[]>
  pendingCount: Ref<number>
  isOnline: Ref<boolean>
  flushing: Ref<boolean>
  enqueue: (type: string, clientEventId: string, payload: Record<string, unknown>) => QueuedAction
  remove: (id: string) => void
  clear: () => void
  flush: () => Promise<void>
}

function readQueue(storageKey: string): QueuedAction[] {
  try {
    const raw = globalThis.localStorage?.getItem(storageKey)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as QueuedAction[]) : []
  } catch {
    return []
  }
}

export function isNetworkError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const candidate = error as { response?: unknown; code?: string }
  return candidate.response == null || candidate.code === 'ERR_NETWORK'
}

export function useOfflineQueue(options: OfflineQueueOptions): UseOfflineQueueReturn {
  const queue = ref<QueuedAction[]>(readQueue(options.storageKey))
  const isOnline = ref(
    typeof navigator === 'undefined' || navigator.onLine === true,
  )
  const flushing = ref(false)
  const pendingCount = ref(queue.value.length)

  function persist(): void {
    try {
      globalThis.localStorage?.setItem(options.storageKey, JSON.stringify(queue.value))
    } catch {
      // queue still applies for this session
    }
    pendingCount.value = queue.value.length
  }

  function enqueue(type: string, clientEventId: string, payload: Record<string, unknown>): QueuedAction {
    const existing = queue.value.find(
      (action) => action.clientEventId === clientEventId && action.type === type,
    )
    if (existing) return existing

    const action: QueuedAction = {
      id: `${type}-${clientEventId}-${Date.now()}`,
      type,
      clientEventId,
      payload,
      createdAt: new Date().toISOString(),
    }
    queue.value = [...queue.value, action]
    persist()
    return action
  }

  function remove(id: string): void {
    if (!queue.value.some((action) => action.id === id)) return
    queue.value = queue.value.filter((action) => action.id !== id)
    persist()
  }

  function clear(): void {
    queue.value = []
    persist()
  }

  async function flush(): Promise<void> {
    if (flushing.value || queue.value.length === 0) return
    flushing.value = true
    const snapshot = [...queue.value]
    try {
      for (const action of snapshot) {
        if (!isOnline.value) break
        try {
          await options.execute(action)
          remove(action.id)
        } catch (error) {
          const retryable = options.isRetryable ? options.isRetryable(error) : true
          if (!retryable) {
            remove(action.id)
          }
          break
        }
      }
    } finally {
      flushing.value = false
    }
  }

  let onlineHandler = (): void => {
    isOnline.value = true
    void flush()
  }
  let offlineHandler = (): void => {
    isOnline.value = false
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('online', onlineHandler)
    window.addEventListener('offline', offlineHandler)
    // Re-attempt anything persisted while offline as soon as we start online.
    if (isOnline.value && queue.value.length > 0) {
      void flush()
    }
  }

  return {
    queue,
    pendingCount,
    isOnline,
    flushing,
    enqueue,
    remove,
    clear,
    flush,
  }
}
